import { useState, useRef, useEffect } from 'react'
import { Filter, Check, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useDashboardStore } from '../stores/dashboardStore'
import { useEmployees } from '../hooks/useEmployees'
import { useOrderTemplates } from '../hooks/useOrderTemplates'
import { useGroupedEmployees } from '../hooks/useGroups'
import { getStatusLabel } from '../utils/statusColors'

type FilterType =
  | { kind: 'dropdown'; column: 'status' | 'employee' | 'template' }
  | { kind: 'date'; column: 'orderDate' | 'deliveryDate' }
  | { kind: 'text'; column: 'customerName' | 'reference' | 'yourRef' | 'info' | 'phone' | 'email' | 'customerId' | 'deliveryName' }
  | { kind: 'range'; column: 'orderId' }

interface ColumnFilterPopoverProps {
  filterType: FilterType
}

const STATUS_OPTIONS = [
  { label: getStatusLabel('import'), value: 'import' },
  { label: getStatusLabel('pending'), value: 'pending' },
  { label: getStatusLabel('partial'), value: 'partial' },
  { label: getStatusLabel('delivered'), value: 'delivered' },
  { label: getStatusLabel('cancelled'), value: 'cancelled' },
  { label: getStatusLabel('archived'), value: 'archived' },
]

export function ColumnFilterPopover({ filterType }: ColumnFilterPopoverProps) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [open])

  const isActive = useIsFilterActive(filterType)

  return (
    <div ref={ref} className="relative inline-flex">
      <button
        onClick={(e) => {
          e.stopPropagation()
          setOpen(!open)
        }}
        className={cn(
          'p-0.5 rounded transition-colors',
          isActive
            ? 'text-emerald-600 bg-emerald-50'
            : 'text-gray-400 hover:text-gray-600 hover:bg-gray-200'
        )}
        title="Filtrer"
      >
        <Filter size={14} fill={isActive ? 'currentColor' : 'none'} />
      </button>

      {open && (
        <div
          className="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-50 py-1"
          style={{ minWidth: filterType.kind === 'date' || filterType.kind === 'range' ? 200 : 180 }}
        >
          {filterType.kind === 'dropdown' && (
            <DropdownFilter column={filterType.column} />
          )}
          {filterType.kind === 'date' && (
            <DateFilter column={filterType.column} />
          )}
          {filterType.kind === 'text' && (
            <TextFilter column={filterType.column} onClose={() => setOpen(false)} />
          )}
          {filterType.kind === 'range' && (
            <RangeFilter />
          )}
        </div>
      )}
    </div>
  )
}

function useIsFilterActive(filterType: FilterType): boolean {
  const statusFilter = useDashboardStore((s) => s.statusFilter)
  const employeeFilter = useDashboardStore((s) => s.employeeFilter)
  const templateFilter = useDashboardStore((s) => s.templateFilter)
  const orderDateFrom = useDashboardStore((s) => s.orderDateFrom)
  const orderDateTo = useDashboardStore((s) => s.orderDateTo)
  const deliveryDateFrom = useDashboardStore((s) => s.deliveryDateFrom)
  const deliveryDateTo = useDashboardStore((s) => s.deliveryDateTo)
  const orderIdFrom = useDashboardStore((s) => s.orderIdFrom)
  const orderIdTo = useDashboardStore((s) => s.orderIdTo)
  const customerNameFilter = useDashboardStore((s) => s.customerNameFilter)
  const referenceFilter = useDashboardStore((s) => s.referenceFilter)
  const yourRefFilter = useDashboardStore((s) => s.yourRefFilter)
  const infoFilter = useDashboardStore((s) => s.infoFilter)
  const phoneFilter = useDashboardStore((s) => s.phoneFilter)
  const emailFilter = useDashboardStore((s) => s.emailFilter)
  const customerIdFilter = useDashboardStore((s) => s.customerIdFilter)
  const deliveryNameFilter = useDashboardStore((s) => s.deliveryNameFilter)

  switch (filterType.kind) {
    case 'dropdown':
      if (filterType.column === 'status') return statusFilter.length > 0
      if (filterType.column === 'employee') return employeeFilter.length > 0
      if (filterType.column === 'template') return templateFilter.length > 0
      return false
    case 'date':
      if (filterType.column === 'orderDate') return !!(orderDateFrom || orderDateTo)
      if (filterType.column === 'deliveryDate') return !!(deliveryDateFrom || deliveryDateTo)
      return false
    case 'range':
      if (filterType.column === 'orderId') return orderIdFrom !== null || orderIdTo !== null
      return false
    case 'text':
      if (filterType.column === 'customerName') return customerNameFilter.length > 0
      if (filterType.column === 'reference') return referenceFilter.length > 0
      if (filterType.column === 'yourRef') return yourRefFilter.length > 0
      if (filterType.column === 'info') return infoFilter.length > 0
      if (filterType.column === 'phone') return phoneFilter.length > 0
      if (filterType.column === 'email') return emailFilter.length > 0
      if (filterType.column === 'customerId') return customerIdFilter.length > 0
      if (filterType.column === 'deliveryName') return deliveryNameFilter.length > 0
      return false
  }
}

// ─── Dropdown filter (status, employee, template) ──────────────────

function DropdownFilter({ column }: { column: 'status' | 'employee' | 'template' }) {
  const { data: employees = [] } = useEmployees()
  const { data: templates = [] } = useOrderTemplates()
  const groupedEmployees = useGroupedEmployees(employees)

  const statusFilter = useDashboardStore((s) => s.statusFilter)
  const setStatusFilter = useDashboardStore((s) => s.setStatusFilter)
  const employeeFilter = useDashboardStore((s) => s.employeeFilter)
  const setEmployeeFilter = useDashboardStore((s) => s.setEmployeeFilter)
  const templateFilter = useDashboardStore((s) => s.templateFilter)
  const setTemplateFilter = useDashboardStore((s) => s.setTemplateFilter)

  let options: { label: string; value: string | number }[]
  let selected: (string | number)[]
  let onToggle: (val: string | number) => void
  let onClear: () => void

  if (column === 'status') {
    options = STATUS_OPTIONS
    selected = statusFilter
    onToggle = (val) => {
      const v = val as string
      setStatusFilter(
        statusFilter.includes(v)
          ? statusFilter.filter((s) => s !== v)
          : [...statusFilter, v]
      )
    }
    onClear = () => setStatusFilter([])
  } else if (column === 'employee') {
    options = employees.map((e) => ({ label: e.full_name, value: e.employee_id }))
    selected = employeeFilter
    onToggle = (val) => {
      const v = val as number
      setEmployeeFilter(
        employeeFilter.includes(v)
          ? employeeFilter.filter((s) => s !== v)
          : [...employeeFilter, v]
      )
    }
    onClear = () => setEmployeeFilter([])
  } else {
    options = templates.map((t) => ({ label: t.name, value: t.order_template_id }))
    selected = templateFilter
    onToggle = (val) => {
      const v = val as number
      setTemplateFilter(
        templateFilter.includes(v)
          ? templateFilter.filter((s) => s !== v)
          : [...templateFilter, v]
      )
    }
    onClear = () => setTemplateFilter([])
  }

  const allSelected = selected.length === 0

  // Grouped employee rendering
  if (column === 'employee' && groupedEmployees) {
    return (
      <div className="max-h-64 overflow-y-auto">
        <button
          onClick={onClear}
          className="flex items-center gap-2 w-full px-3 py-1.5 text-[12px] text-left hover:bg-gray-50"
        >
          <div
            className={cn(
              'w-3.5 h-3.5 rounded border flex items-center justify-center',
              allSelected ? 'bg-emerald-500 border-emerald-500' : 'border-gray-300'
            )}
          >
            {allSelected && <Check size={10} className="text-white" />}
          </div>
          Alle
        </button>
        <div className="h-px bg-gray-100 my-0.5" />
        {groupedEmployees.map((group, i) => {
          const prev = i > 0 ? groupedEmployees[i - 1] : null
          const showSectionHeader = !prev || prev.groupType !== group.groupType
          return (
          <div key={group.groupName}>
            {showSectionHeader && group.groupType === 'department' && (
              <div className="px-3 pt-2 pb-0.5 text-[9px] font-bold uppercase tracking-widest text-gray-300">Avdelinger</div>
            )}
            {showSectionHeader && group.groupType === 'employee' && (
              <>
                <div className="h-px bg-gray-100 my-1" />
                <div className="px-3 pt-1 pb-0.5 text-[9px] font-bold uppercase tracking-widest text-gray-300">Grupper</div>
              </>
            )}
            {showSectionHeader && group.groupType === 'ungrouped' && (
              <div className="h-px bg-gray-100 my-1" />
            )}
            <button
              onClick={() => {
                const groupIds = group.items.map((i) => i.value)
                const allInGroupSelected = groupIds.every((id) => selected.includes(id))
                if (allInGroupSelected) {
                  // Deselect all in group
                  const remaining = (selected as number[]).filter((id) => !groupIds.includes(id))
                  setEmployeeFilter(remaining)
                } else {
                  // Select all in group
                  const merged = [...new Set([...(selected as number[]), ...groupIds])]
                  setEmployeeFilter(merged)
                }
              }}
              className="px-3 py-1 text-[10px] font-semibold uppercase tracking-wider text-gray-400 hover:text-gray-600 flex items-center gap-1.5 w-full text-left hover:bg-gray-50 transition-colors"
            >
              {group.groupColor && (
                <span
                  className="w-2 h-2 rounded-full flex-shrink-0"
                  style={{ backgroundColor: group.groupColor }}
                />
              )}
              {group.groupName}
              {group.items.every((i) => selected.includes(i.value)) && (
                <Check size={10} className="text-emerald-500 ml-auto" />
              )}
            </button>
            {group.items.map((opt) => {
              const isSelected = selected.includes(opt.value)
              return (
                <button
                  key={opt.value}
                  onClick={() => onToggle(opt.value)}
                  className="flex items-center gap-2 w-full px-3 py-1.5 text-[12px] text-left hover:bg-gray-50 pl-5"
                >
                  <div
                    className={cn(
                      'w-3.5 h-3.5 rounded border flex items-center justify-center',
                      isSelected ? 'bg-emerald-500 border-emerald-500' : 'border-gray-300'
                    )}
                  >
                    {isSelected && <Check size={10} className="text-white" />}
                  </div>
                  {opt.label}
                </button>
              )
            })}
          </div>
        )})}
      </div>
    )
  }

  // Default flat list for status, template, and employees without groups
  return (
    <div className="max-h-64 overflow-y-auto">
      <button
        onClick={onClear}
        className="flex items-center gap-2 w-full px-3 py-1.5 text-[12px] text-left hover:bg-gray-50"
      >
        <div
          className={cn(
            'w-3.5 h-3.5 rounded border flex items-center justify-center',
            allSelected ? 'bg-emerald-500 border-emerald-500' : 'border-gray-300'
          )}
        >
          {allSelected && <Check size={10} className="text-white" />}
        </div>
        Alle
      </button>
      <div className="h-px bg-gray-100 my-0.5" />
      {options.map((opt) => {
        const isSelected = selected.includes(opt.value)
        return (
          <button
            key={String(opt.value)}
            onClick={() => onToggle(opt.value)}
            className="flex items-center gap-2 w-full px-3 py-1.5 text-[12px] text-left hover:bg-gray-50"
          >
            <div
              className={cn(
                'w-3.5 h-3.5 rounded border flex items-center justify-center',
                isSelected ? 'bg-emerald-500 border-emerald-500' : 'border-gray-300'
              )}
            >
              {isSelected && <Check size={10} className="text-white" />}
            </div>
            {opt.label}
          </button>
        )
      })}
    </div>
  )
}

// ─── Range filter (orderId from/to) ─────────────────────────────────

function RangeFilter() {
  const setOrderIdRange = useDashboardStore((s) => s.setOrderIdRange)
  const from = useDashboardStore((s) => s.orderIdFrom)
  const to = useDashboardStore((s) => s.orderIdTo)

  return (
    <div className="px-3 py-2 space-y-2">
      <label className="block text-[11px] text-gray-500 font-medium">Fra</label>
      <input
        type="number"
        autoFocus
        placeholder="F.eks. 700000"
        value={from ?? ''}
        onChange={(e) => {
          const val = e.target.value ? Number(e.target.value) : null
          setOrderIdRange(val, to)
        }}
        className="w-full px-2 py-1.5 text-[12px] border border-gray-200 rounded focus:outline-none focus:border-emerald-400 tabular-nums"
      />
      <label className="block text-[11px] text-gray-500 font-medium">Til</label>
      <input
        type="number"
        placeholder="F.eks. 710000"
        value={to ?? ''}
        onChange={(e) => {
          const val = e.target.value ? Number(e.target.value) : null
          setOrderIdRange(from, val)
        }}
        className="w-full px-2 py-1.5 text-[12px] border border-gray-200 rounded focus:outline-none focus:border-emerald-400 tabular-nums"
      />
      {(from !== null || to !== null) && (
        <button
          onClick={() => setOrderIdRange(null, null)}
          className="flex items-center gap-1 text-[11px] text-gray-400 hover:text-gray-600"
        >
          <X size={10} />
          Nullstill
        </button>
      )}
    </div>
  )
}

// ─── Date filter ────────────────────────────────────────────────────

function dateYearsAgo(years: number): string {
  const d = new Date()
  d.setFullYear(d.getFullYear() - years)
  return d.toISOString().slice(0, 10)
}

const DATE_PRESETS = [
  { label: 'Siste år', years: 1 },
  { label: 'Siste 3 år', years: 3 },
  { label: 'Siste 5 år', years: 5 },
]

function DateFilter({ column }: { column: 'orderDate' | 'deliveryDate' }) {
  const setDateRange = useDashboardStore((s) => s.setDateRange)
  const from = useDashboardStore((s) =>
    column === 'orderDate' ? s.orderDateFrom : s.deliveryDateFrom
  )
  const to = useDashboardStore((s) =>
    column === 'orderDate' ? s.orderDateTo : s.deliveryDateTo
  )

  return (
    <div className="px-3 py-2 space-y-2">
      <div className="flex gap-1">
        {DATE_PRESETS.map((p) => (
          <button
            key={p.years}
            onClick={() => setDateRange(column, dateYearsAgo(p.years), null)}
            className={cn(
              'px-2 py-0.5 text-[11px] rounded-full border transition-colors',
              from === dateYearsAgo(p.years) && !to
                ? 'bg-emerald-50 border-emerald-300 text-emerald-700'
                : 'border-gray-200 text-gray-500 hover:border-gray-300'
            )}
          >
            {p.label}
          </button>
        ))}
      </div>
      <label className="block text-[11px] text-gray-500 font-medium">Fra</label>
      <input
        type="date"
        value={from ?? ''}
        onChange={(e) => setDateRange(column, e.target.value || null, to)}
        className="w-full px-2 py-1 text-[12px] border border-gray-200 rounded focus:outline-none focus:border-emerald-400"
      />
      <label className="block text-[11px] text-gray-500 font-medium">Til</label>
      <input
        type="date"
        value={to ?? ''}
        onChange={(e) => setDateRange(column, from, e.target.value || null)}
        className="w-full px-2 py-1 text-[12px] border border-gray-200 rounded focus:outline-none focus:border-emerald-400"
      />
      {(from || to) && (
        <button
          onClick={() => setDateRange(column, null, null)}
          className="flex items-center gap-1 text-[11px] text-gray-400 hover:text-gray-600"
        >
          <X size={10} />
          Nullstill
        </button>
      )}
    </div>
  )
}

// ─── Text filter ────────────────────────────────────────────────────

function TextFilter({
  column,
  onClose,
}: {
  column: 'customerName' | 'reference' | 'yourRef' | 'info' | 'phone' | 'email' | 'customerId' | 'deliveryName'
  onClose: () => void
}) {
  const setTextFilter = useDashboardStore((s) => s.setTextFilter)
  const value = useDashboardStore((s) => {
    switch (column) {
      case 'customerName': return s.customerNameFilter
      case 'reference': return s.referenceFilter
      case 'yourRef': return s.yourRefFilter
      case 'info': return s.infoFilter
      case 'phone': return s.phoneFilter
      case 'email': return s.emailFilter
      case 'customerId': return s.customerIdFilter
      case 'deliveryName': return s.deliveryNameFilter
    }
  })

  return (
    <div className="px-3 py-2 space-y-1.5">
      <input
        type="text"
        autoFocus
        placeholder="Skriv for å filtrere..."
        value={value}
        onChange={(e) => setTextFilter(column, e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Escape') onClose()
        }}
        className="w-full px-2 py-1.5 text-[12px] border border-gray-200 rounded focus:outline-none focus:border-emerald-400"
      />
      {value && (
        <button
          onClick={() => setTextFilter(column, '')}
          className="flex items-center gap-1 text-[11px] text-gray-400 hover:text-gray-600"
        >
          <X size={10} />
          Nullstill
        </button>
      )}
    </div>
  )
}
